import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { CARD_TYPES, TYPE_META } from "@/lib/types";

const EMPTY = {
  name: "",
  url: "",
  type: "other",
  plant: "",
  username: "",
  password: "",
  notes: "",
};

const inputCls =
  "w-full border-b border-hairline bg-transparent py-3 font-mono text-sm transition-colors placeholder:text-ink/30 focus:border-ink focus:outline-none";
const labelCls =
  "font-mono text-[11px] tracking-[0.2em] text-ink/60 mt-6 block first:mt-0";

export function CardFormModal({ open, card, onSave, onDelete, onClose }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(
        card
          ? {
              name: card.name || "",
              url: card.url || "",
              type: card.type || "other",
              plant: card.plant || "",
              username: card.username || "",
              password: card.password || "",
              notes: card.notes || "",
            }
          : EMPTY
      );
      setConfirmDelete(false);
      window.__lenis?.stop();
      document.body.style.overflow = "hidden";
    } else {
      window.__lenis?.start();
      document.body.style.overflow = "";
    }
    return () => {
      window.__lenis?.start();
      document.body.style.overflow = "";
    };
  }, [open, card]);

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    const url = form.url.trim();
    if (!name || !url) {
      toast.error("Name and URL are required");
      return;
    }
    const fullUrl = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    setSaving(true);
    try {
      await onSave(
        { ...form, name, url: fullUrl, type: form.type },
        card?.id
      );
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    await onDelete(card.id);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={onClose}
          data-testid="card-form-modal-overlay"
          className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 backdrop-blur-sm sm:items-center sm:p-6"
        >
          <motion.div
            key="panel"
            initial={{ opacity: 0, y: 40, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            data-testid="card-form-modal"
            className="max-h-[92vh] w-full max-w-lg overflow-y-auto border border-ink bg-paper p-6 md:p-8"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="flex items-center gap-2.5 font-mono text-[11px] tracking-[0.25em] text-ink/60">
                  <span className="h-2 w-2 bg-signal" />
                  DASHBOARD CARD
                </p>
                <h3 className="mt-3 font-display text-2xl font-extrabold tracking-tight">
                  {card ? "EDIT CARD" : "NEW DASHBOARD CARD"}
                </h3>
              </div>
              <button
                data-testid="close-modal-button"
                onClick={onClose}
                aria-label="Close"
                className="border border-hairline p-2 text-ink/60 transition-colors hover:border-ink hover:text-ink"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <form onSubmit={submit} noValidate>
              <label className={labelCls} htmlFor="card-name">
                DASHBOARD NAME *
              </label>
              <input
                id="card-name"
                data-testid="card-name-input"
                value={form.name}
                onChange={set("name")}
                placeholder="Line 3 OEE Board"
                className={inputCls}
                autoFocus
              />

              <label className={labelCls} htmlFor="card-url">
                DASHBOARD URL *
              </label>
              <input
                id="card-url"
                data-testid="card-url-input"
                value={form.url}
                onChange={set("url")}
                placeholder="grafana.yourplant.com/d/line3"
                className={inputCls}
              />

              <span className={labelCls}>PLATFORM TYPE</span>
              <div className="mt-2 flex flex-wrap gap-2">
                {CARD_TYPES.map((t) => (
                  <button
                    key={t}
                    type="button"
                    data-testid={`type-chip-${t}`}
                    onClick={() => setForm((f) => ({ ...f, type: t }))}
                    className={`border px-3 py-2 font-mono text-[11px] tracking-[0.15em] transition-colors duration-200 ${
                      form.type === t
                        ? "border-ink bg-ink text-paper"
                        : "border-hairline text-ink/60 hover:border-ink hover:text-ink"
                    }`}
                  >
                    {TYPE_META[t].label}
                  </button>
                ))}
              </div>

              <label className={labelCls} htmlFor="card-plant">
                PLANT / LOCATION
              </label>
              <input
                id="card-plant"
                data-testid="card-plant-input"
                value={form.plant}
                onChange={set("plant")}
                placeholder="Pump Station B"
                className={inputCls}
              />

              <label className={labelCls} htmlFor="card-username">
                USERNAME
              </label>
              <input
                id="card-username"
                data-testid="card-username-input"
                value={form.username}
                onChange={set("username")}
                placeholder="ops_viewer"
                className={inputCls}
                autoComplete="off"
              />

              <label className={labelCls} htmlFor="card-password">
                PASSWORD
              </label>
              <input
                id="card-password"
                data-testid="card-password-input"
                type="text"
                value={form.password}
                onChange={set("password")}
                placeholder="Saved on the card, hidden until you reveal it"
                className={inputCls}
                autoComplete="off"
              />

              <label className={labelCls} htmlFor="card-notes">
                NOTES
              </label>
              <textarea
                id="card-notes"
                data-testid="card-notes-input"
                value={form.notes}
                onChange={set("notes")}
                rows={2}
                placeholder="Anything worth remembering — VPN required, shift timings…"
                className={`${inputCls} resize-none`}
              />

              <div className="mt-8 flex items-center gap-3">
                <button
                  type="submit"
                  data-testid="save-card-button"
                  disabled={saving}
                  className="inline-flex flex-1 items-center justify-center gap-2 bg-ink px-6 py-3.5 font-mono text-[11px] tracking-[0.2em] text-paper transition-colors duration-300 hover:bg-signal disabled:opacity-60"
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : card ? (
                    "SAVE CHANGES"
                  ) : (
                    "ADD TO THE DECK"
                  )}
                </button>
                {card ? (
                  <button
                    type="button"
                    data-testid="delete-card-button"
                    onClick={doDelete}
                    className={`inline-flex items-center gap-2 border px-4 py-3.5 font-mono text-[11px] tracking-[0.15em] transition-colors duration-200 ${
                      confirmDelete
                        ? "border-signal bg-signal text-paper"
                        : "border-hairline text-ink/60 hover:border-signal hover:text-signal"
                    }`}
                  >
                    <Trash2 className="h-4 w-4" />
                    {confirmDelete ? "SURE?" : ""}
                  </button>
                ) : null}
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
